import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { GeneralButton } from "../../common/ui/GeneralButton";

export const PORTAL_PREVIEW_URL = "https://preview.portal.bonisoft.de";

const HIGHLIGHT_KEYS = ["highlight1", "highlight2", "highlight3", "highlight4"];

interface PortalShutdownNoticeDialogProps {
  open: boolean;
  /** Schließen ohne Bestätigung (X, Escape, Klick außerhalb) - Hinweis erscheint erneut. */
  onClose: () => void;
  /** Dauerhaft bestätigen - Hinweis erscheint für diesen Nutzer nicht mehr. */
  onConfirm: () => void;
}

export default function PortalShutdownNoticeDialog({
  open,
  onClose,
  onConfirm,
}: PortalShutdownNoticeDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      // Klick auf den Hintergrund schließt nicht - nur X, Escape oder "Bestätigen".
      onClose={(_event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pr: 6 }}>
        {t("portalShutdownNotice.title")}
        <IconButton
          aria-label={t("portalShutdownNotice.closeAriaLabel")}
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography>{t("portalShutdownNotice.intro")}</Typography>

          <Typography sx={{ fontWeight: 600 }}>{t("portalShutdownNotice.headline")}</Typography>
          {HIGHLIGHT_KEYS.map((key) => (
            <Stack key={key} spacing={0.5}>
              <Typography sx={{ fontWeight: 600 }}>
                {t(`portalShutdownNotice.${key}Title`)}
              </Typography>
              <Typography>{t(`portalShutdownNotice.${key}Text`)}</Typography>
            </Stack>
          ))}

          <Typography sx={{ fontWeight: 600 }}>
            {t("portalShutdownNotice.discoverLabel")}
          </Typography>
          <Typography>{t("portalShutdownNotice.discoverText")}</Typography>
          <Typography>{t("portalShutdownNotice.shutdownInfo")}</Typography>
          <Typography>{t("portalShutdownNotice.urlInfo")}</Typography>

          <GeneralButton
            component="a"
            href={PORTAL_PREVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ alignSelf: "flex-start" }}
          >
            {t("portalShutdownNotice.openPortal")}
          </GeneralButton>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ flexDirection: "column", alignItems: "stretch", gap: 1, px: 3, pb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t("portalShutdownNotice.confirmHint")}
        </Typography>
        <GeneralButton onClick={onConfirm} autoFocus sx={{ alignSelf: "flex-end" }}>
          {t("buttons.confirm")}
        </GeneralButton>
      </DialogActions>
    </Dialog>
  );
}
